import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState, { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { FolderLock, Upload, Download, Trash2, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
    { value: "identity", label: "Identity (PAN, Aadhaar…)" },
    { value: "property", label: "Property" },
    { value: "legal", label: "Legal (Will, POA)" },
    { value: "policy", label: "Insurance / Policy" },
    { value: "investment", label: "Investment" },
    { value: "other", label: "Other" },
];

export default function Vault() {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("all");

    const [file, setFile] = useState(null);
    const [category, setCategory] = useState("identity");
    const [expiry, setExpiry] = useState("");
    const [notes, setNotes] = useState("");
    const [tags, setTags] = useState("");
    const [autoTag, setAutoTag] = useState(true);
    const [busy, setBusy] = useState(false);
    const [analyzingId, setAnalyzingId] = useState(null);

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/documents");
        setDocs(data);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const onUpload = async (e) => {
        e.preventDefault();
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("category", category);
            if (expiry) fd.append("expiry_date", expiry);
            if (notes) fd.append("notes", notes);
            if (tags) fd.append("tags", tags);
            if (autoTag) fd.append("auto_tag", "true");
            const { data } = await api.post("/documents/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (data?.ai_suggestion?.doc_type) {
                toast.success(`Uploaded · AI tagged as "${data.ai_suggestion.doc_type}"`);
            } else {
                toast.success("Document uploaded");
            }
            setFile(null); setNotes(""); setTags(""); setExpiry("");
            setOpen(false);
            load();
        } catch (e2) {
            toast.error(e2.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const onDownload = async (doc) => {
        try {
            const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = doc.original_filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (_e) {
            toast.error("Download failed");
        }
    };

    const onDelete = async (doc) => {
        if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
        await api.delete(`/documents/${doc.id}`);
        toast.success("Deleted");
        load();
    };

    const onReanalyze = async (doc) => {
        setAnalyzingId(doc.id);
        try {
            const { data } = await api.post(`/documents/${doc.id}/ocr?apply=true`);
            const dt = data?.suggestion?.doc_type;
            toast.success(dt ? `AI re-tagged as "${dt}"` : "Updated with AI suggestions");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI analysis failed");
        } finally {
            setAnalyzingId(null);
        }
    };

    const filtered = filter === "all" ? docs : docs.filter((d) => d.category === filter);

    return (
        <div data-testid="vault-page">
            <PageHeader
                eyebrow="Document Vault"
                title="Encrypted, tagged, never lost."
                subtitle="PAN, Aadhaar, Will, policies — uploaded once, found forever."
                testid="vault-header"
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="upload-doc-btn" className="rounded-full">
                                <Upload className="mr-2 h-4 w-4" /> Upload document
                            </Button>
                        </DialogTrigger>
                        <DialogContent data-testid="upload-doc-dialog">
                            <DialogHeader>
                                <DialogTitle>Upload to vault</DialogTitle>
                                <DialogDescription>
                                    PDFs and images up to 25MB. Enable AI auto-tag to let Claude classify and read expiry dates.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={onUpload} className="space-y-4">
                                <div>
                                    <Label htmlFor="file">File (max 25MB)</Label>
                                    <Input
                                        id="file" type="file" required
                                        data-testid="upload-file-input"
                                        onChange={(e) => setFile(e.target.files?.[0])}
                                    />
                                </div>
                                <div>
                                    <Label>Category</Label>
                                    <Select value={category} onValueChange={setCategory}>
                                        <SelectTrigger data-testid="upload-category"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map((c) => (
                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="expiry">Expiry date (optional)</Label>
                                    <Input
                                        id="expiry" type="date"
                                        data-testid="upload-expiry"
                                        value={expiry} onChange={(e) => setExpiry(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="tags">Tags (comma separated)</Label>
                                    <Input id="tags" value={tags} onChange={(e) => setTags(e.target.value)}
                                           data-testid="upload-tags" placeholder="india, identity, primary" />
                                </div>
                                <div>
                                    <Label htmlFor="notes">Notes</Label>
                                    <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                                              data-testid="upload-notes" />
                                </div>
                                <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer">
                                    <Checkbox
                                        checked={autoTag}
                                        onCheckedChange={(v) => setAutoTag(!!v)}
                                        data-testid="upload-auto-tag"
                                    />
                                    <div>
                                        <div className="text-sm font-semibold flex items-center gap-1.5">
                                            <Sparkles className="h-3.5 w-3.5 text-accent" /> AI auto-tag
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            Claude reads the file and fills in category, expiry, and tags. Adds ~5s.
                                        </div>
                                    </div>
                                </label>
                                <DialogFooter>
                                    <Button type="submit" disabled={busy} data-testid="upload-submit" className="rounded-full">
                                        {busy ? "Uploading…" : "Upload"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                }
            />

            <div className="px-8 py-8 space-y-6">
                {/* Filter pills */}
                <div className="flex flex-wrap gap-2">
                    <button
                        data-testid="filter-all"
                        onClick={() => setFilter("all")}
                        className={`pill ${filter === "all" ? "bg-primary text-primary-foreground border-primary" : ""}`}
                    >
                        All
                    </button>
                    {CATEGORIES.map((c) => (
                        <button
                            key={c.value}
                            data-testid={`filter-${c.value}`}
                            onClick={() => setFilter(c.value)}
                            className={`pill ${filter === c.value ? "bg-primary text-primary-foreground border-primary" : ""}`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                {loading ? <LoadingBlock /> : filtered.length === 0 ? (
                    <EmptyState
                        icon={FolderLock}
                        title="No documents yet"
                        body="Start with PAN, Aadhaar, passport, and your insurance policy schedules. Add expiry dates so we can remind you."
                        testid="vault-empty"
                    />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vault-grid">
                        {filtered.map((d) => (
                            <article
                                key={d.id}
                                data-testid={`doc-card-${d.id}`}
                                className="rounded-lg border border-border bg-card p-5 flex flex-col"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="rounded-md bg-secondary p-2 text-primary">
                                        <FileText className="h-5 w-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold truncate">{d.original_filename}</div>
                                        <div className="font-mono text-xs text-muted-foreground mt-1">
                                            {(d.size / 1024).toFixed(1)} KB · {d.content_type?.split("/")[1] || "file"}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap items-center gap-2">
                                    <Badge variant="secondary" className="capitalize">{d.category}</Badge>
                                    {d.expiry_date ? (
                                        <Badge variant="outline" className="font-mono">exp {d.expiry_date}</Badge>
                                    ) : null}
                                    {(d.tags || []).slice(0, 2).map((t) => (
                                        <Badge key={t} variant="outline">{t}</Badge>
                                    ))}
                                </div>
                                {d.notes ? <p className="mt-3 text-xs text-muted-foreground line-clamp-2">{d.notes}</p> : null}
                                <div className="mt-auto pt-4 flex items-center gap-2">
                                    <Button
                                        size="sm" variant="outline"
                                        data-testid={`doc-download-${d.id}`}
                                        onClick={() => onDownload(d)}
                                    >
                                        <Download className="h-3.5 w-3.5 mr-1" /> Download
                                    </Button>
                                    <Button
                                        size="sm" variant="ghost"
                                        data-testid={`doc-reocr-${d.id}`}
                                        disabled={analyzingId === d.id}
                                        onClick={() => onReanalyze(d)}
                                        title="Re-analyze with AI"
                                    >
                                        <Sparkles className={`h-3.5 w-3.5 ${analyzingId === d.id ? "animate-pulse text-accent" : "text-accent"}`} />
                                    </Button>
                                    <Button
                                        size="sm" variant="ghost"
                                        data-testid={`doc-delete-${d.id}`}
                                        onClick={() => onDelete(d)}
                                    >
                                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                    </Button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
